import React from 'react';
import { Box, Timeline } from '@mantine/core';
import * as TablerIcons from '@tabler/icons-react';
import PuckInlineRichText from '../../proposal/ProposalRichText/PuckInlineRichText';
import { textRoleCss } from '../../../puck/fields/TextRolesField';
import ProposalBoxAlign from '../../proposal/ProposalBoxAlign/ProposalBoxAlign';
import { puckArrayFieldPath } from '../../../utils/puckContentPath';
import { borderCssFromProps } from '../../../../shared/boxes/borderStyles';
import { boxShadowCss } from '../../../../shared/boxes/shadowStyles';
import { cssLengthToPx } from '../../../utils/proposalUnits';
import {
  resolveTimelineLayout, timelineAccentColor, timelineConnector,
} from './timelineLayout';

const DynamicIcon = ({ name, size = 16 }) => {
  const Icon = TablerIcons[name] || TablerIcons.IconPoint;
  return <Icon size={size} stroke={1.5} />;
};

// Hors édition, un texte simple se rend dans un <span> : sans display block,
// date, titre et description se suivraient sur une même ligne.
const DATE_STYLE = {
  display: 'block',
  fontSize: 'var(--mantine-font-size-xs)',
  color: 'var(--mantine-color-dimmed)',
  fontWeight: 400,
  lineHeight: 1.4,
};

const TITLE_STYLE = { display: 'block' };

const DESCRIPTION_STYLE = { display: 'block', marginTop: 4, fontSize: 'var(--mantine-font-size-sm)' };

/**
 * Bloc Puck — Frise chronologique. Box unitaire d'une colonne de section
 * (règle 1 box par colonne). Date, titre et description s'éditent sur le
 * canvas ; la sidebar ne porte que les attributs (icône, fonds, disposition).
 */
const TimelineBlock = ({
  id,
  items = [],
  /** Disposition — cf. `timelineLayout.js`. */
  layout = 'vertical-left',
  /** Couleur CSS des puces et du trait — vide = couleur principale du thème. */
  color = '',
  /** Diamètre des puces — longueur CSS (champ px↔rem) ; Mantine attend un px. */
  bulletSize = '1.5rem',
  border = null,
  backgroundColor = '',
  /* Clé de l'échelle partagée ; 'none' pour une frise déjà en base. */
  shadow = 'none',
  alignHorizontal = 'center',
  alignVertical = 'center',
  /** Style par défaut des textes, par rôle (`date`, `title`, `description`). */
  textStyles = null,
  puck,
}) => {
  const isEditing = !!puck?.isEditing;
  const bulletPx = cssLengthToPx(bulletSize) || 24;
  const { orientation, align, textPosition } = resolveTimelineLayout(layout);

  const dateNode = (item, idx) => (isEditing || item.date) && (
    <PuckInlineRichText
      id={id}
      puck={puck}
      html={item.date}
      contentPath={puckArrayFieldPath('items', idx, 'date')}
      style={{ ...DATE_STYLE, ...textRoleCss(textStyles, 'date') }}
      minHeight={isEditing ? 20 : undefined}
      placeholder="Date"
      reducedToolbar
    />
  );

  const titleNode = (item, idx) => (
    <PuckInlineRichText
      id={id}
      puck={puck}
      html={item.title}
      contentPath={puckArrayFieldPath('items', idx, 'title')}
      style={{ ...TITLE_STYLE, ...textRoleCss(textStyles, 'title') }}
      minHeight={isEditing ? 24 : undefined}
      placeholder="Titre"
      reducedToolbar
    />
  );

  const descriptionNode = (item, idx) => (isEditing || item.description) && (
    <PuckInlineRichText
      id={id}
      puck={puck}
      html={item.description}
      contentPath={puckArrayFieldPath('items', idx, 'description')}
      style={{ ...DESCRIPTION_STYLE, ...textRoleCss(textStyles, 'description') }}
      minHeight={isEditing ? 48 : undefined}
      placeholder="Description"
    />
  );

  const accent = timelineAccentColor(color);

  const bulletIcon = (item) => (item.icon
    ? <DynamicIcon name={item.icon} size={Math.round(bulletPx * 0.6)} />
    : undefined);

  // `active` = toutes les étapes : sans ça Mantine ne peint `--tl-color` sur
  // aucune puce ni aucun trait, et la couleur choisie reste invisible.
  // `itemBullet` : Mantine ne remplit la puce que si elle a un enfant (icône) ;
  // sans icône elle resterait blanche à trait coloré.
  const vertical = (
    <Timeline
      color={color || undefined}
      bulletSize={bulletPx}
      lineWidth={2}
      align={align}
      active={items.length}
      styles={{ itemBullet: { backgroundColor: accent } }}
    >
      {items.map((item, idx) => (
        <Timeline.Item
          key={idx}
          bullet={bulletIcon(item)}
          title={(
            <>
              {dateNode(item, idx)}
              {titleNode(item, idx)}
            </>
          )}
        >
          {descriptionNode(item, idx)}
        </Timeline.Item>
      ))}
    </Timeline>
  );

  const textRow = textPosition === 'top' ? 1 : 2;
  const bulletRow = textPosition === 'top' ? 2 : 1;

  // Grille : les puces occupent leur propre ligne, donc restent alignées
  // quelle que soit la hauteur des textes.
  const horizontal = (
    <Box style={{ overflowX: 'auto' }}>
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${items.length || 1}, minmax(140px, 1fr))`,
          rowGap: 8,
        }}
      >
        {items.map((item, idx) => {
          const { before, after } = timelineConnector(idx, items.length);
          return (
            <Box
              key={`bullet-${idx}`}
              style={{
                gridRow: bulletRow,
                gridColumn: idx + 1,
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center',
              }}
            >
              <Box style={{ height: 2, backgroundColor: before ? accent : 'transparent' }} />
              <Box
                style={{
                  width: bulletPx,
                  height: bulletPx,
                  borderRadius: '50%',
                  border: `2px solid ${accent}`,
                  backgroundColor: accent,
                  color: 'var(--mantine-color-white)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {bulletIcon(item)}
              </Box>
              <Box style={{ height: 2, backgroundColor: after ? accent : 'transparent' }} />
            </Box>
          );
        })}
        {items.map((item, idx) => (
          <Box
            key={`text-${idx}`}
            style={{
              gridRow: textRow,
              gridColumn: idx + 1,
              alignSelf: textPosition === 'top' ? 'end' : 'start',
              padding: '0 8px',
              textAlign: 'center',
            }}
          >
            {dateNode(item, idx)}
            <Box style={{ fontWeight: 500 }}>{titleNode(item, idx)}</Box>
            {descriptionNode(item, idx)}
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <ProposalBoxAlign horizontal={alignHorizontal} vertical={alignVertical} innerMode="full">
      <Box
        p="md"
        style={{
          ...borderCssFromProps(border),
          ...(backgroundColor ? { backgroundColor } : {}),
          ...(boxShadowCss(shadow) ? { boxShadow: boxShadowCss(shadow) } : {}),
        }}
      >
        {orientation === 'horizontal' ? horizontal : vertical}
      </Box>
    </ProposalBoxAlign>
  );
};

export default TimelineBlock;
